import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { format, compareDesc } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowRight
} from 'react-icons/md';

const HistoryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ShowMoreButtonWrapper = styled.button`
  font-size: 0.75rem;
  width: 100%;
  font-weight: 400;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #656f7d;
  display: flex;
  padding: 0;
  align-items: center;
  border-radius: 0.25rem;
  height: 1.5rem;
  gap: 0.5rem;

  &:hover {
    background-color: #f6f9fc;
    color: #656f7d;
  }
`;

const HistoryItemWrapper = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.1rem;
  max-width: calc(100% - 1.1rem);
  width: 100%;
  font-size: 0.75rem;
  font-weight: 400;
  color: #656f7d;
  position: relative;
  padding: 0.5rem 0;

  & > span {
    font-size: 0.625rem;
    color: #656f7d;
  }

  & > .history-info-date {
    min-width: 30%;
    display: flex;
    font-size: 0.75rem;
    justify-content: flex-end;
  }

  &::before {
    content: '';
    width: 0.25rem;
    height: 0.25rem;
    border-radius: 100%;
    background-color: #656f7d;
    position: absolute;
    left: -0.75rem;
    top: calc(50% - 0.125rem);
  }
`;

const HistoryList = styled.ul`
  list-style: disc;
  padding-left: 1rem;
  margin-bottom: 0.2rem;
`;

const HistoryItem = ({ item }) => {
  const formattedDate = format(
    new Date(item.createdAt),
    `dd 'de' LLLL 'às' HH:mm`,
    { locale: ptBR }
  );

  return (
    <HistoryItemWrapper>
      {item.changeDescription}
      <div className="history-info-date">
        <span>{formattedDate}</span>
      </div>
    </HistoryItemWrapper>
  );
};

const ShowMoreButton = ({ isExpanded, onClick }) => {
  return (
    <ShowMoreButtonWrapper onClick={onClick} type="button">
      {isExpanded ? (
        <MdOutlineKeyboardArrowDown style={{ width: '1rem', height: '1rem' }} />
      ) : (
        <MdOutlineKeyboardArrowRight
          style={{ width: '1rem', height: '1rem' }}
        />
      )}
      {isExpanded ? 'Ocultar' : 'Mostrar mais'}
    </ShowMoreButtonWrapper>
  );
};

/**
 * Componente para exibir um histórico de alterações simplificado.
 *
 * @component
 * @param {Object} props - Propriedades do componente.
 * @param {Array} props.data - Lista de itens do histórico. Cada item deve conter:
 *   @param {string} props.data[].createdAt - Data de criação do item no formato ISO.
 *   @param {string} props.data[].changeDescription - Descrição da alteração realizada.
 *
 * @returns {JSX.Element} Um componente que exibe o histórico de alterações.
 */
const ShowHistoryChanges = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortedData, setSortedData] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      const sorted = [...data].sort((a, b) =>
        compareDesc(new Date(b.createdAt), new Date(a.createdAt))
      );
      setSortedData(sorted);

      setIsExpanded(data.length === 1);
    } else {
      setSortedData([]);
    }
  }, [data]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  if (!data || data.length === 0) {
    return <div>Nenhum histórico disponível.</div>;
  }

  return (
    <HistoryWrapper>
      {data.length > 1 && (
        <ShowMoreButton isExpanded={isExpanded} onClick={toggleExpand} />
      )}

      <HistoryList
        style={{
          display: isExpanded || data.length === 1 ? 'block' : 'none'
        }}
      >
        {(isExpanded || data.length === 1) &&
          sortedData.map((item) => <HistoryItem key={item.id} item={item} />)}
      </HistoryList>
    </HistoryWrapper>
  );
};

export default ShowHistoryChanges;
